import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box
} from '@mui/material';
import SortIndicator from './sortable-column-head';

/**
 * @typedef {Object} ProductivityRow
 * @property {string} userId - The unique identifier for the agent
 * @property {number} productive - Number of productive calls
 * @property {string} telecaller - Name of the telecaller/agent
 */

/**
 * ProductivityCountTable - A sortable table component for displaying telecaller productivity counts
 * 
 * This component renders a Material-UI table with telecaller productivity data. The first column
 * (Telecaller) is sticky and displays the telecaller name. The productive column is sortable.
 * 
 * @param {Object} props - Component props
 * @param {ProductivityRow[]} props.rows - Array of productivity data objects
 * @param {string} [props.maxHeight='100%'] - Maximum height of the table container
 * @returns {JSX.Element} The rendered table component
 */
const ProductivityCountTable = ({
  rows = [],
  maxHeight = '100%'
}) => {
  // Internal sorting state
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null
  });

  /**
   * Handles sorting when the productive column header is clicked
   * @param {string} columnKey - The key of the column to sort
   * @param {('asc'|'desc'|null)} direction - The sort direction
   */
  const handleSort = (columnKey, direction) => {
    setSortConfig({
      key: columnKey,
      direction: direction
    });
  };

  /**
   * Creates a sort map for the SortIndicator component
   * @returns {Object} Object with sort direction for the productive column
   */
  const sortMap = useMemo(() => {
    const map = {
      'Productive': null
    };

    if (sortConfig.key === 'Productive') {
      map['Productive'] = sortConfig.direction;
    }

    return map;
  }, [sortConfig]);

  /**
   * Sorts the rows based on current sort configuration
   * @returns {ProductivityRow[]} Sorted array of rows
   */
  const sortedRows = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction || sortConfig.key !== 'Productive') {
      return rows;
    }

    return [...rows].sort((a, b) => {
      const aValue = a.productive || 0;
      const bValue = b.productive || 0;

      // Handle numeric sorting for productive values
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [rows, sortConfig]);

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      {/* Title */}
      <Typography
        sx={{
          fontWeight: 'bold',
          color: '#F28627',
          fontSize: '1em',
          mb: 1.5
        }}
      >
        Productive count of telecallers
      </Typography>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: maxHeight,
          overflowY: 'auto',
          boxShadow: 'none',
          border: 'none'
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {/* Telecaller Column */}
              <TableCell
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 1001,
                  backgroundColor: 'white',
                  borderBottom: '1px solid rgba(224, 224, 224, 1)',
                  minWidth: '8rem'
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 'bold',
                    color: '#F28627',
                    fontSize: '0.9em'
                  }}
                >
                  Telecaller
                </Typography>
              </TableCell>

              {/* Sortable Productive Column */}
              <TableCell
                align="right"
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 1001,
                  backgroundColor: 'white',
                  borderBottom: '1px solid rgba(224, 224, 224, 1)',
                  minWidth: '6rem'
                }}
              >
                <SortIndicator
                  direction={sortMap['Productive']}
                  label="Productive"
                  onClick={handleSort}
                />
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedRows.map((row, index) => (
              <TableRow key={row.userId || index} hover>
                {/* Telecaller Name Cell */}
                <TableCell
                  sx={{
                    borderBottom: '1px solid rgba(240, 240, 240, 1)',
                    py: 1
                  }}
                >
                  <Typography
                    sx={{
                      color: 'rgba(0, 0, 0, 0.7)',
                      fontSize: '0.9em'
                    }}
                  >
                    {row.telecaller || 'Unknown Agent'}
                  </Typography>
                </TableCell>

                {/* Productive Count Cell */}
                <TableCell
                  align="right"
                  sx={{
                    borderBottom: '1px solid rgba(240, 240, 240, 1)',
                    py: 1,
                    pr: 2
                  }}
                >
                  <Typography
                    sx={{
                      color: 'rgba(0, 0, 0, 0.7)',
                      fontSize: '0.9em'
                    }}
                  >
                    {row.productive || '0'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}

            {/* Empty state */}
            {sortedRows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={2}
                  align="center"
                  sx={{ py: 4 }}
                >
                  <Typography
                    sx={{
                      color: 'rgba(0, 0, 0, 0.5)',
                      fontSize: '1em'
                    }}
                  >
                    No productivity data available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ProductivityCountTable;
